import React, { useEffect } from "react";
import styled from "styled-components";
import { Lock, FileText } from "lucide-react";
import { useLocation } from "react-router-dom";
import Navbar from "../../components/Navbar";
import LogoP from "../../assets/LogoImage.png";
import theme from "../../theme";
import Footer from "../../components/Footer";

const Container = styled.div`
  max-width: 1200px;
  margin: 50px auto;
  padding: 2rem;
  font-family:
    system-ui,
    -apple-system,
    sans-serif;
`;

const Content = styled.div`
  background: ${theme.colors.text.quaternary};
  border: 1px solid ${theme.colors.text.quinary};
  border-radius: 20px;
  padding: 2rem;
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
`;

const Section = styled.section`
  margin-bottom: 2rem;
  line-height: 1.7;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
  color: ${theme.colors.primary};
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Paragraph = styled.p`
  margin-bottom: 1.5rem;
  color: ${theme.colors.text.secondary};
`;

const Term = styled.span`
  font-weight: 600;
  color: ${theme.colors.primary};
`;

const ImportantNote = styled.div`
  background-color: ${theme.colors.background.main};
  border-left: 4px solid ${theme.colors.primary};
  padding: 1.5rem;
  margin: 2rem 0;
  border-radius: 0.5rem;
`;

const List = styled.ul`
  list-style-type: decimal;
  margin-left: 1.5rem;
  margin-bottom: 1.5rem;
`;

const ListItem = styled.li`
  margin-bottom: 1rem;
  color: ${theme.colors.text.secondary};
`;

const Divider = styled.hr`
  border: 0;
  height: 1px;
  background: #e5e7eb;
  margin: 2rem 0;
`;

function TermsAndConditions() {
  const location = useLocation();

  useEffect(() => {
    const sectionId = location.pathname.replace("/", "");
    if (sectionId) {
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) {
          section.scrollIntoView({ behavior: "smooth" });
        }
      }, 100); // Delay ensures section is rendered before scrolling
    }
  }, [location.pathname]);

  const pages = ["Home", "Product", "Contact Us"];
  const userOptions = ["Profile", "Settings", "Logout"];

  const handlePageSelect = (page) => {
    if (!page) {
      console.error("Page is null or undefined");
      return;
    }

    const sectionId = page.toLowerCase().replace(/\s+/g, "-");
    const section = document.getElementById(sectionId);

    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
      window.history.pushState(null, "", `/${sectionId}`);
    } else {
      console.warn(`Section with ID '${sectionId}' not found.`);
      window.location.href = `/${sectionId}`;
    }
  };

  return (
    <>
      <Navbar
        logo={LogoP}
        pages={pages}
        userOptions={userOptions}
        onPageSelect={handlePageSelect}
        // onUserOptionSelect={handleUserOptionSelect}
      />
      <Container>
        <Content>
          <Section>
            <SectionTitle>
              <FileText size={28} />
              Terms & Conditions
            </SectionTitle>

            <Paragraph>
              These Terms & Conditions of (a) use of our website
              https://prakalpa.ai, our applications or any products or services
              in connection with the Application/, Website/products or (b) any
              modes of registrations or usage of products, including through
              web, tablets or other device are between Innovative Logic Lab pvt
              ltd.
            </Paragraph>

            <Paragraph>
              These Terms constitute an electronic record in accordance with the
              provisions of the Information Technology Act, 2000 and the
              Information Technology Rules, 2011 thereunder, as amended from
              time to time.
            </Paragraph>

            <ImportantNote>
              <Paragraph style={{ margin: 0 }}>
                Please read the Terms and the privacy policy of the Company (
                <Term>"Privacy Policy"</Term>) with respect to registration with
                us, the use of the Application, Website, Services, and products
                carefully before using the Application, Website, Services or
                products. In the event of any discrepancy between the Terms and
                any other policies with respect to the Application or Website or
                Services or products, the provisions of the Terms shall prevail.
              </Paragraph>
            </ImportantNote>

            <Paragraph>
              Your use/access/browsing of the Application or Website or the
              Services or products or registration (with or without payment/with
              or without a subscription) through any means shall signify Your
              acceptance of the Terms and Your agreement to be legally bound by
              the same.
            </Paragraph>

            <Paragraph>
              If You do not agree with the Terms or the Privacy Policy, please
              do not use the Application or Website or avail the Services or
              products. Any access to our Services/Application/products through
              registrations/subscription is non-transferable.
            </Paragraph>

            <List id="privacypolicy">
              <ListItem>
                Except as mentioned below, all information, content, material,
                trademarks, services marks, trade names, and trade secrets
                including but not limited to the software, text, images,
                graphics, video, script and audio, contained in the Application,
                Website, Services and products are proprietary property of the
                Company (<Term>"Proprietary Information"</Term>). No Proprietary
                Information may be copied, downloaded, reproduced, modified,
                republished, uploaded, posted, transmitted or distributed in any
                way without obtaining prior written permission from the Company
                and nothing on this Application or Website or Services shall be
                or products deemed to confer a license of or any other right,
                interest or title to or in any of the intellectual property
                rights belonging to the Company.
              </ListItem>
            </List>
          </Section>

          <Divider />

          <Section>
            <SectionTitle>
              <Lock size={28} />
              Privacy Policy
            </SectionTitle>

            <Paragraph>
              At Prakalpa, we take your privacy seriously. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your
              information when you visit our website or use our services.
            </Paragraph>

            <List>
              <ListItem>
                <Term>Information We Collect</Term>: We collect information that
                you voluntarily provide to us when you register for an account,
                express interest in obtaining information about us or our
                products and services, or otherwise contact us.
              </ListItem>

              <ListItem>
                <Term>How We Use Your Information</Term>: We use the information
                we collect to provide, maintain, and improve our services, to
                develop new ones, and to protect Prakalpa and our users.
              </ListItem>

              <ListItem>
                <Term>Information Security</Term>: We implement appropriate
                technical and organizational measures to maintain the safety of
                your personal information.
              </ListItem>

              <ListItem>
                <Term>Third-Party Disclosure</Term>: We do not sell, trade, or
                otherwise transfer your personally identifiable information to
                third parties without your consent, except as required by law.
              </ListItem>

              <ListItem>
                <Term>Cookies and Tracking Technologies</Term>: We use cookies
                and similar tracking technologies to track activity on our
                Website and hold certain information to improve and analyze our
                Service.
              </ListItem>
            </List>

            <ImportantNote>
              <Paragraph style={{ margin: 0 }}>
                By using our Website and Services, you consent to our Privacy
                Policy and agree to its terms. If we decide to change our
                privacy policy, we will post those changes on this page.
              </Paragraph>
            </ImportantNote>
          </Section>
        </Content>
      </Container>
      <Footer />
    </>
  );
}

export default TermsAndConditions;
